import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { extractApiErrorMessage, notifications } from '../../utils';
import { DashboardService } from '../../hooks/DashboardService';
import {
  DashboardAnimalData,
  DashboardSource,
  DashboardViewModel,
  OWN_FARM_SOURCE,
  isOwnFarmSource
} from '../../models/dashboard';
import DashboardAnimalCard, { DashboardAnimalCardSkeleton } from '../../components/DashboardAnimalCard';
import { useTranslation } from 'react-i18next';
import { Container } from '@material-ui/core';
import { getDashboardConfigByFarmType } from '../../utils';
import { PigletPeriodSelect, PigletStatCards, RegisteredPigletsChart } from '../../components/PigletRegistration';
import { BirthWeightChart } from '../../components/WeightMilestones';
import { SlaughterDeliveryChart, SlaughterSummaryCards } from '../../components/SlaughterForecast';
import DeviceSyncStatus from '../../components/DeviceSyncStatus';
import FarmBenchmarkCard from '../../components/FarmBenchmark';
import { usePigletRegistration } from '../../hooks/usePigletRegistration';
import { useWeightMilestones } from '../../hooks/useWeightMilestones';
import { useSlaughterForecast } from '../../hooks/useSlaughterForecast';
import { useFarmBenchmark } from '../../hooks/useFarmBenchmark';
import { DEFAULT_PIGLET_PERIOD_WEEKS } from '../../models/piglet-registration';
import { getBirthWeightSeries } from '../../models/weight-milestones';

export interface DashboardProps {
  /**
   * Which farm to render. Defaults to the signed-in business unit, which is what a farm user always gets;
   * the admin farms overview passes another farm so it can reuse this page instead of growing a second one.
   */
  source?: DashboardSource;
  /** Where the "another farm" banner's back control returns to. Ignored for the user's own dashboard. */
  backPath?: string;
}

/**
 * Names the farm being viewed and offers the way back. Shown only when the dashboard is pointed at a farm
 * other than the signed-in one: without it the page is indistinguishable from the admin's own dashboard,
 * which is how someone ends up reading another farm's numbers as their own.
 */
const ForeignFarmBanner = ({ source, backPath }: { source: DashboardSource; backPath?: string }) => {
  const { t } = useTranslation();

  const title = source.farmName ?? t('f-farms-overview-unnamed-farm');

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-indigo-700">{t('f-dashboard-viewing-farm')}</p>
        <p className="mt-0.5 truncate text-sm font-semibold text-indigo-950">
          {source.farmNumber ? `${title} · ${source.farmNumber}` : title}
        </p>
      </div>

      {backPath && (
        <Link
          to={backPath}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-indigo-200 bg-white px-3 text-sm font-medium text-indigo-700 no-underline shadow-sm transition-colors hover:bg-indigo-100"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('f-farms-overview-back')}
        </Link>
      )}
    </div>
  );
};

/**
 * Reload control for the whole page. Not the shared ReloadIconButton: that one draws its glyph with the
 * Material Icons ligature font and carries MUI's 48px hit box, which next to the period select showed up
 * as either nothing or a stray word at the wrong height. This is an inline SVG at the select's own 36px
 * square, so the pair reads as one segmented control.
 *
 * Every native button style is reset explicitly - Tailwind is loaded here without preflight, so a bare
 * <button> otherwise keeps the browser's grey face and border.
 */
const RefreshButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      title={t('f-reload')}
      aria-label={t('f-reload')}
      className="flex h-9 w-9 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-md border border-gray-200 bg-white p-0 text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <RefreshCw className="h-4 w-4" aria-hidden="true" />
    </button>
  );
};

/**
 * Heading for one of the dashboard's two blocks. Both were unlabelled, which left the reader to work out
 * from the cards what each row of numbers was about. Same title/subtitle type scale as the chart card
 * below, so the page reads as one set of sections rather than three unrelated headings.
 */
const SectionHeader = ({ title, subtitle, action }: { title: string; subtitle: string; action?: React.ReactNode }) => (
  <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
    <div className="min-w-0">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
    </div>

    {action}
  </div>
);

const Dashboard: React.FC<DashboardProps> = ({ source = OWN_FARM_SOURCE, backPath }) => {
  const [dashboard, setDashboard] = React.useState({} as DashboardViewModel);
  // `loaded` is the only loading flag left: it swaps the animal cards for their skeletons, and the reload
  // button clears it. A separate isBusy would have driven a spinner that duplicates those skeletons.
  const [loaded, setLoaded] = React.useState(false);
  const { t } = useTranslation();

  const farmId = source.farmId;
  const isOwnFarm = isOwnFarmSource(source);

  const [periodWeeks, setPeriodWeeks] = React.useState(DEFAULT_PIGLET_PERIOD_WEEKS);
  const {
    dashboard: pigletDashboard,
    isBusy: pigletsBusy,
    isEnabled: pigletsEnabled,
    reload: reloadPiglets
  } = usePigletRegistration(periodWeeks, farmId);

  // Its own fetch rather than a field on the piglet widget's: that response buckets by date of birth and
  // this one by the day the pig was weighed, and one payload carrying both rules reads as neither.
  const {
    dashboard: weightDashboard,
    isBusy: weightsBusy,
    reload: reloadWeights
  } = useWeightMilestones(periodWeeks, farmId);

  // Its own fetch too: it buckets by slaughter date, and only farms with a slaughter source get it at all.
  const {
    dashboard: slaughterDashboard,
    isBusy: slaughterBusy,
    isEnabled: slaughterEnabled,
    reload: reloadSlaughter
  } = useSlaughterForecast(periodWeeks, farmId);

  // Not driven by the period select: the benchmark is a nightly snapshot over a fixed twelve months.
  const {
    benchmark,
    isBusy: benchmarkBusy,
    loaded: benchmarkLoaded,
    reload: reloadBenchmark
  } = useFarmBenchmark(farmId);

  React.useEffect(() => {
    if (loaded) {
      return;
    }

    // Navigating from one farm's dashboard straight to another's keeps this component mounted, so the
    // answer to the previous farm can still be in flight; ignore anything but the newest request rather
    // than letting it land under the new farm's name.
    let cancelled = false;

    new DashboardService()
      .getDashboard(farmId)
      .then((dashboard) => {
        if (cancelled) return;
        setDashboard(dashboard);
      })
      .catch((e) => {
        if (cancelled) return;
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [loaded, farmId]);

  // The farm is part of what was loaded, so changing it invalidates the data the same way the reload
  // button does.
  React.useEffect(() => {
    setLoaded(false);
  }, [farmId]);

  // The farm type decides how many cards there are, and it arrives with the data - so the skeleton guesses
  // the common case of a four-stage pig farm rather than rendering nothing until the response lands.
  const renderAnimalCardSkeletons = () => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((index) => (
        <DashboardAnimalCardSkeleton key={index} />
      ))}
    </div>
  );

  const renderDashboardContent = () => {
    const config = getDashboardConfigByFarmType(dashboard.farmType);

    const candidates: (DashboardAnimalData | false | undefined)[] = config.isPigFarm
      ? [
          config.displaySow && dashboard.sowData,
          config.displayGilt && dashboard.giltData,
          config.displayPiglet && dashboard.pigletData,
          config.displayFattening && dashboard.fatteningData
        ]
      : [
          config.displayCow && dashboard.cowData,
          config.displayCalf && dashboard.calfData,
          config.displayGoat && dashboard.goatData
        ];

    const animals = candidates.filter(Boolean) as DashboardAnimalData[];

    if (animals.length === 0) {
      return null;
    }

    // A Tailwind grid rather than the MUI one so these cards share a gutter with the piglet cards below
    // and line up column for column instead of sitting on a slightly wider 24px rhythm.
    const gridClass = config.isPigFarm
      ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'
      : 'grid grid-cols-1 gap-4 md:grid-cols-2';

    return (
      <div className={gridClass}>
        {animals.map((animalData) => (
          <DashboardAnimalCard key={animalData.animalType} animalData={animalData} />
        ))}
      </div>
    );
  };

  // Resolved before the JSX so the section - heading included - can be dropped entirely for a farm whose
  // type shows no animal cards at all, rather than leaving a heading over nothing.
  const animalCards = loaded ? renderDashboardContent() : renderAnimalCardSkeletons();

  // Reloads every section, so it rides in whichever control row is on screen first - the piglet chart's
  // when that section exists, since that is where the period select is and the two belong on one line.
  const reloadButton = (
    <RefreshButton
      onClick={() => {
        setLoaded(false);
        reloadPiglets();
        reloadWeights();
        reloadSlaughter();
        reloadBenchmark();
      }}
    />
  );

  return (
    <>
      {/* The app shell already caps and pads the page at xl; matching it here and dropping the nested
          gutters gives the dashboard the full width instead of a second lg-wide box inside it. */}
      <Container maxWidth="xl" disableGutters>
        {!isOwnFarm && <ForeignFarmBanner source={source} backPath={backPath} />}

        {/* No page title and no page-level control row - the section headings carry both the names and the
            controls. No spinner either: every section has a skeleton, so it would only duplicate them.
            Order is the farm's reading order: the weekly chart is what the page is opened for, the piglet
            totals explain it, and the medical figures are the standing reference underneath. */}
        {/* Every chart card carries its own title, so this row is only the controls. The period selected
            here drives both weekly charts and the piglet cards, which is why it sits above all of them
            rather than inside the piglet section it started in. */}
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-end gap-2">
            <PigletPeriodSelect value={periodWeeks} onChange={setPeriodWeeks} />
            {reloadButton}
          </div>

          {pigletsEnabled && <RegisteredPigletsChart dashboard={pigletDashboard} isBusy={pigletsBusy} />}
        </section>

        {/* Not behind the piglet widget's opt-out: that switch hides the registration figures, and the
            birth weights are a separate question. */}
        <section className="mb-8">
          <BirthWeightChart series={getBirthWeightSeries(weightDashboard)} isBusy={weightsBusy} />
        </section>

        {pigletsEnabled && (
          <section className="mb-8">
            <SectionHeader title={t('f-piglets-reg-section-title')} subtitle={t('f-piglets-reg-section-subtitle')} />

            <PigletStatCards dashboard={pigletDashboard} isBusy={pigletsBusy} />
          </section>
        )}

        {/* After the piglet sections because it is their consequence: the piglets registered there are the
            pigs due here 24 weeks later. Absent entirely on a farm with no slaughter source. */}
        {slaughterEnabled && slaughterDashboard && (
          <section className="mb-8">
            <SectionHeader title={t('f-slaughter-section-title')} subtitle={t('f-slaughter-section-subtitle')} />

            <div className="grid gap-4">
              <SlaughterSummaryCards dashboard={slaughterDashboard} isBusy={slaughterBusy} />
              <SlaughterDeliveryChart
                weeks={slaughterDashboard.weeks}
                hasBirthCohorts={slaughterDashboard.hasBirthCohorts}
                slaughterAgeWeeks={slaughterDashboard.slaughterAgeWeeks}
                mortalityPercent={slaughterDashboard.expectedMortalityPercent}
                isBusy={slaughterBusy}
              />
            </div>
          </section>
        )}

        {/* After the weekly sections: it is the twelve-month view of the same farm against the others, read
            once the current weeks are understood. */}
        <section className="mb-8">
          <FarmBenchmarkCard benchmark={benchmark} isBusy={benchmarkBusy} loaded={benchmarkLoaded} />
        </section>

        {animalCards && (
          <section className="mb-8">
            <SectionHeader title={t('f-dashboard-title')} subtitle={t('f-dashboard-subtitle')} />

            {animalCards}
          </section>
        )}

        {/* Last, and with no section heading of its own - the card carries a card-scale title instead,
            because it is a footnote on the figures above rather than a fourth set of them. It removes
            itself entirely on a farm whose phones have never reported, so it costs nothing there. Its own
            reload control is deliberate: the page reload button drives the sections that share a farm
            fetch, and this one has its own. */}
        <DeviceSyncStatus farmId={farmId} />
      </Container>
    </>
  );
};

export default Dashboard;
